import dayjs from "dayjs"
import advancedFormat from "dayjs/plugin/advancedFormat"
import relativeTimePlugin from "dayjs/plugin/relativeTime"
import localeData from "dayjs/plugin/localeData"
import utcPlugin from "dayjs/plugin/utc"
import timezonePlugin from "dayjs/plugin/timezone"
import weekday from "dayjs/plugin/weekday"
import { CLOCK_FORMAT_TWELVE_HOUR_SETTING } from "./generalSettingsHelpers"

dayjs.extend(localeData)
dayjs.extend(utcPlugin)
dayjs.extend(timezonePlugin)
dayjs.extend(relativeTimePlugin)
dayjs.extend(advancedFormat)
dayjs.extend(weekday)

export const dayjsOrgTz = (dateTime) => dayjs(dateTime).tz(dayjs.tz().$x.$timezone)
export const dayjsWithTz = (dateTime, timezone) => dayjs(dateTime).tz(timezone)

export const YEAR_UNIT = "year"
export const QUARTER_UNIT = "quarter"
export const MONTH_UNIT = "month"
export const WEEK_UNIT = "week"
export const DAY_UNIT = "day"
export const HOUR_UNIT = "hour"
export const MINUTE_UNIT = "minute"
export const SECOND_UNIT = "second"

const TWELVE_HOUR_FORMAT = "hh:mm A"
// e.g. 02:34 PM

const TWENTY_FOUR_HOUR_FORMAT = "HH:mm"
// e.g. 13:05

const WEEKDAY_DATE_FORMAT = "ddd, MMM D, YYYY"
// e.g. Mon, Jun 13 2023

export const DAYS = "days"
export const WEEKS = "weeks"
export const MONTHS = "months"
export const RANGE_UNIT_OPTIONS = [DAYS, WEEKS, MONTHS]

export const daysOfTheWeek = dayjs.weekdays()
// ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

export const TWENTY_FOUR_HOUR_TIME_REGEX = /^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/
// HH:MM 24-hour format

export const tomorrowDate = () => dayjsOrgTz().add(1, "d").format("MM/DD/YY")
export const yesterdayDateWithTimezone = (timezone) => dayjs().add(-1, "d").tz(timezone).format("YYYY-MM-DD")

export const numberDate = (dateInput) => dayjsOrgTz(dateInput).format("MM/DD/YY")
export const unpaddedNumberDate = (dateInput) => dayjsOrgTz(dateInput).format("M/D/YY")
// e.g. 3/8/21
export const shortMonthDate = (dateInput, convertToOrgTz = true) => {
  if (convertToOrgTz) {
    return dayjsOrgTz(dateInput).format("MMM D, YYYY")
  }

  return dayjs(dateInput).format("MMM D, YYYY")
}
// e.g. Mar 8, 2021

export const weekDayMonthDate = (dateInput) => dayjsOrgTz(dateInput).format("dddd, MMM DD")
// e.g. Tuesday, Mar 08

export const shortDayMonthDateYear = (dateInput) => dayjsOrgTz(dateInput).format("ddd, MMMM D, YYYY")

export const monthDate = (dateInput) => dayjsOrgTz(dateInput).format("MMM D")
// e.g. Mar 8

export const fullMonthDate = (dateInput) => dayjsOrgTz(dateInput).format("MMMM D")
// e.g. March 8

export const yearMonthDayDate = (dateInput) => dayjsOrgTz(dateInput).format("YYYY-MM-DD")
export const yearMonthDayDateWithTz = (dateInput, timezone) => dayjsWithTz(dateInput, timezone).format("YYYY-MM-DD")
// e.g. 2022-06-15

export const yearMonthDayDatetime = (dateTime) => dayjsOrgTz(dateTime).format("YYYY-MM-DD HH:mm ZZ")
// e.g. 2022-06-15 14:33 -0400

export const thisYear = () => new Date().getUTCFullYear()

export const isToday = (date) => dayjsOrgTz().isSame(date, DAY_UNIT)

export const todayDay = () => dayjsOrgTz().format("dddd")

export const todayDayOfMonth = () => dayjsOrgTz().format("D")

// '01' because January has 31 days.
const maxMonthDateRange = Array.from({ length: dayjs("01").daysInMonth() }, (_, index) => index + 1);

export const dateOrdinal = (number) => dayjs(`1-${number}`).format("Do")

export const dateOrdinalOptions = () => maxMonthDateRange.map((number) => (
  { label: dateOrdinal(number), value: number }
))

export const timeByClockFormat = (time, clockFormat) => {
  // 1/1/1 because dayjs requires date to convert a time
  const dateWithTime = dayjs(`1/1/1 ${time}`)

  switch (clockFormat) {
  case CLOCK_FORMAT_TWELVE_HOUR_SETTING:
    return dateWithTime.format(TWELVE_HOUR_FORMAT)
  default:
    return dateWithTime.format(TWENTY_FOUR_HOUR_FORMAT)
  }
}

export const orgLocalDateTimeByClockFormat = (dateTime, clockFormat) => {
  const orgDateTime = dayjsOrgTz(dateTime)

  switch (clockFormat) {
  case CLOCK_FORMAT_TWELVE_HOUR_SETTING:
    return orgDateTime.format(`${WEEKDAY_DATE_FORMAT} [at] ${TWELVE_HOUR_FORMAT}`)
    // e.g. Mon, Jun 13 2023 at 05:19 PM
  default:
    return orgDateTime.format(`${WEEKDAY_DATE_FORMAT} [at] ${TWENTY_FOUR_HOUR_FORMAT}`)
    // e.g. Mon, Jun 13 2023 at 17:19
  }
}
